import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline, GlobalStyles, Stack, Box, CircularProgress } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { theme } from './theme';
import store from './redux/store';
import Header from './components/Header';
import InstagramBrowserBanner from './components/InstagramBrowserBanner';
import LandingPage from './pages/LandingPage';

const SigninPage = lazy(() => import('./pages/SigninPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const LearnPage = lazy(() => import('./pages/LearnPage'));
const BlogsPage = lazy(() => import('./pages/BlogsPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const NakshatraPage = lazy(() => import('./pages/learn/NakshatraPage'));
const DashaPage = lazy(() => import('./pages/learn/DashaPage'));
const GocharPage = lazy(() => import('./pages/learn/GocharPage'));
const VargaPage = lazy(() => import('./pages/learn/VargaPage'));
const YogasPage = lazy(() => import('./pages/learn/YogasPage'));
const MatchPage = lazy(() => import('./pages/MatchPage'));
const MatchChatPage = lazy(() => import('./pages/MatchChatPage'));
const MatchInvitePage = lazy(() => import('./pages/MatchInvitePage'));

import { setUser, logout } from './redux/userSlice';
import { isTokenExpired, setupAutoLogout } from './utils/authUtils';
import { getProfile } from './services/authService';
import { acceptInvite } from './services/compatService';
import { setLocaleFromCountry, setFreeCredits } from './utils/localeUtils';

// Initialize locale from browser language before any render
setLocaleFromCountry(null);

// Capture ?ref= promo code from any landing URL so it survives navigation to /signup.
// sessionStorage (not localStorage) — it must not outlive this browsing session, otherwise
// a stale code left by one visitor silently gets applied to a different person signing up
// later on the same shared device/browser.
try {
  const refCode = new URLSearchParams(window.location.search).get('ref');
  if (refCode) {
    sessionStorage.setItem(
      'pendingRefCode',
      JSON.stringify({ code: refCode.toUpperCase(), ts: Date.now() })
    );
  }
} catch { /* ignore */ }

const AppLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn, status: userStatus } = useSelector((s) => s.user);
  const [appLoading, setAppLoading] = useState(true);

  // Post-auth kundli-match invite acceptance. MatchInvitePage stashes the invite
  // token before sending the user to signup/signin; once they're an active user,
  // accept it here (works for both the new-signup and existing-user paths).
  useEffect(() => {
    if (!isLoggedIn || userStatus === 'temp') return;
    let stored;
    try { stored = JSON.parse(sessionStorage.getItem('pendingInviteToken')); } catch { /* ignore */ }
    if (!stored?.token) return;
    // Expire stale tokens after 1 day to avoid surprising redirects.
    if (Date.now() - (stored.ts || 0) > 86400000) {
      sessionStorage.removeItem('pendingInviteToken');
      return;
    }
    sessionStorage.removeItem('pendingInviteToken');
    // Move into the match context immediately (no /chat + verify-banner flash),
    // then to the specific match once it's computed.
    navigate('/match', { replace: true });
    acceptInvite(stored.token)
      .then((match) => navigate(`/match/${match.matchId}`, { replace: true }))
      .catch(() => { /* invite gone/expired — stay on /match */ });
  }, [isLoggedIn, userStatus, navigate]);

  useEffect(() => {
    const hydrateUser = async () => {
      // Detect country once, then use localStorage forever
      const savedCountry = localStorage.getItem('countryOverride');
      if (savedCountry) {
        setLocaleFromCountry(savedCountry);
      } else {
        try {
          const BASE_URL = (process.env.REACT_APP_API_BASE_URL || window.location.origin) + '/api/auth';
          const localeRes = await fetch(`${BASE_URL}/locale`);
          if (localeRes.ok) {
            const { country, freeCredits } = await localeRes.json();
            if (country) localStorage.setItem('countryOverride', country);
            setLocaleFromCountry(country);
            setFreeCredits(freeCredits);
          }
        } catch { /* fallback to navigator.language */ }
      }

      const token = localStorage.getItem('token');
      if (token && !isTokenExpired(token)) {
        try {
          const user = await getProfile();
          dispatch(setUser({ ...user, isLoggedIn: true }));
          setupAutoLogout(store, token);
        } catch {
          // Don't remove token here — authenticatedFetch already handles 401/403.
          // This catch fires on transient errors (network issues, fetch abort during navigation).
        }
      } else {
        localStorage.removeItem('token');
        dispatch(logout());
      }
      setAppLoading(false);
    };
    hydrateUser();
  }, [dispatch]);

  if (appLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <InstagramBrowserBanner />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Stack
      direction="column"
      spacing={0}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'stretch',
        height: '100dvh',
        overflow: 'hidden'
      }}
    >
      <InstagramBrowserBanner />
      <Header/>
      <Box
        sx={{
          flex: 1,
          width: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          alignItems: 'stretch',
          px: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: '100%',
            maxWidth: 'none',
            minHeight: 0,
            display: 'flex',
          }}
        >
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}><CircularProgress /></Box>}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/signin" element={<SigninPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/how-it-works" element={<ComparisonPage />} />
              <Route path="/how-are-we-different" element={<ComparisonPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/match" element={<MatchPage />} />
              <Route path="/match/invite/:token" element={<MatchInvitePage />} />
              <Route path="/match/:matchId" element={<MatchChatPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              <Route path="/learn" element={<LearnPage />} />
              <Route path="/blogs" element={<BlogsPage />} />
              <Route path="/blogs/:slug" element={<BlogPostPage />} />
              <Route path="/learn/nakshatra/:slug" element={<NakshatraPage />} />
              <Route path="/learn/astrology/vimshottari-dasha" element={<DashaPage />} />
              <Route path="/learn/astrology/gochar" element={<GocharPage />} />
              <Route path="/learn/astrology/divisional-charts" element={<VargaPage />} />
              <Route path="/learn/astrology/yogas" element={<YogasPage />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </Stack>
  );
};

const AppContent = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{
        '@media (max-width: 899px)': {
          '*': {
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          },
        },
      }} />
      <Router>
        <AppLayout />
      </Router>
    </ThemeProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </GoogleOAuthProvider>
  </HelmetProvider>
);

export default App;
